import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule } from '@angular/forms';

import { distinctUntilChanged, map, takeUntil } from 'rxjs';

import { DefaultIcon, SDate, ScreenBreakpointPX } from '@summit/core';
import {
  DateRange,
  DateRangeType,
  DayOfWeek,
  IUserExperience,
  SummitConfig,
  SummitDisposing,
  SummitUserExperienceService,
  Week
} from '@summit/models';
import { LayoutService } from '@summit/ui';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';

@Component({
  selector: 's-form-input-week-range',
  templateUrl: './s-form-input-week-range.component.html',
  styleUrls: ['./s-form-input-week-range.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, DatePicker, Button, FloatLabelModule, InputGroupAddonModule, InputGroupModule]
})
export class SFormInputWeekRangeComponent extends SummitDisposing implements OnInit {
  @Input() labelLocation: 'over' | 'in' | 'on' = 'over';
  @Input() label: string | undefined;
  @Input() required: boolean;
  @Input() size: 'small' | 'large';

  @Input() defaultWeekOffset = 0;
  @Input() defaultRange: DateRange;
  @Input() defaultRangeType: DateRangeType = DateRangeType.TenantWeek;
  @Input() showWeekNumber = false;
  @Input() format = 'm/d/y';
  @ViewChild('calendar') calendar: DatePicker;
  @Output()
  DateRangeChange: EventEmitter<DateRange> = new EventEmitter<DateRange>();
  private dateRangeFocus = false;
  private newWeekEnd: DayOfWeek;
  private currentDateRange: DateRange | null = null;
  public fieldName = 'weekRange';
  public controlValue: Date[];
  public rangeValue: DateRange;
  private calendarVisible = false;
  defaultIcon = DefaultIcon;
  ScreenBreakpoint = ScreenBreakpointPX;

  constructor(
    fb: FormBuilder,
    private uxService: SummitUserExperienceService<IUserExperience>,
    public layoutService: LayoutService
  ) {
    super();
  }

  ngOnInit() {
    // console.log(
    //   `[Week Range Picker] Initializing with ${this.defaultRange?.begin?.format(
    //     'MM/DD'
    //   )} & ${this.defaultRange?.end?.format('MM/DD')}`,
    //   this.defaultRange,
    //   this.defaultRangeType
    // );
    this.defaultRangeType = this.defaultRangeType ?? DateRangeType.TenantWeek;
    //console.log(`[RangePicker] Default Range Type: ${this.defaultRangeType}`, this.defaultRange);
    if (this.defaultRange) {
      //console.log(`[RangePicker] calling updateRange with default range`);
      this.updateRange(this.defaultRange);
    } else {
      const selWeek = new Week(this.defaultWeekOffset, this.defaultRangeType);
      //console.log(`[RangePicker] calling updateRange`);
      this.updateRange(selWeek.ToDateRange(this.defaultRangeType));
    }
    //Handle anything but the picker
    if (this.defaultRangeType != DateRangeType.SettlementWeek) {
      this.uxService.currentUserExperience
        .pipe(
          map((ux) => {
            switch (this.defaultRangeType) {
              case DateRangeType.IVMRWeek:
                return ux?.endOfIvmrWeek ?? SummitConfig.defaultEndOfIvmrWeek;
              case DateRangeType.ScheduleWeek:
                return ux?.endOfScheduleWeek ?? SummitConfig.defaultEndOfScheduleWeek;
              default:
                return ux?.endOfWeek ?? SummitConfig.defaultEndOfWeek;
            }
          }),
          distinctUntilChanged(),
          takeUntil(this.disposing$)
        )
        .subscribe((endOfWeek) => {
          const currentValue: DateRange = new DateRange(this.rangeValue);
          const currentEndOfWeek = currentValue.end?.day();
          if (currentEndOfWeek != endOfWeek && endOfWeek != this.newWeekEnd) {
            this.newWeekEnd = endOfWeek;
            // console.log(`[RangePicker] Current End of Week: ${currentEndOfWeek} migrating to  ${newUx.endOfWeek}`);
            //console.log(`Updating Date Range: Current Value ${currentValue?.begin?.format('MM/DD')} & ${currentValue?.end?.format('MM/DD')}`);
            // console.log(`Got User change with a value set, now comparing New:${newUx.endOfWeek} to Old:${currentEndOfWeek}`);
            if (endOfWeek != currentEndOfWeek) {
              // console.log(`Updating Week`);
              const currentRangeBeginWeek = new Week(currentValue.begin, this.defaultRangeType, currentEndOfWeek);
              //Subtracting a day to keep week in correct week vs. the following week.
              const currentRangeEndWeek = new Week(currentValue.end.add(-1), this.defaultRangeType, currentEndOfWeek);
              const beginOffset = currentRangeBeginWeek.GetWeekOffset();
              const endOffset = currentRangeEndWeek.GetWeekOffset();
              // console.log(
              //   `[RangePicker] Converted to Offset of ${currentRangeBeginWeek.start.format(
              //     'MM/DD'
              //   )}: ${beginOffset} & ${currentRangeEndWeek.end.format('MM/DD')}: ${endOffset}`,
              //   currentRangeBeginWeek,
              //   currentRangeEndWeek,
              //   this.defaultRangeType
              // );
              const newBeginWeek = new Week(beginOffset, this.defaultRangeType, endOfWeek);
              const newEndWeek = new Week(endOffset, this.defaultRangeType, endOfWeek);
              //console.log(`UX: Updating with ${newBeginWeek.start.format('MM/DD')} & ${newEndWeek.end.format('MM/DD')}`);
              //console.log(`[RangePicker] calling updateRange`);
              this.ProcessRangeChange(newBeginWeek, newEndWeek);
            }
            this.newWeekEnd = null;
          }
        });
    }
  }

  public onShow() {
    this.calendarVisible = true;
  }
  public onClose() {
    this.calendarVisible = false;
  }

  public ProcessRangeChange(startWeek?: Week, endWeek?: Week) {
    // console.log(
    //   `[RangePicker] Processing Range Change`,
    //   startWeek,
    //   endWeek,
    //   this.rangeValue.begin.format(),
    //   this.rangeValue.end.format()
    // );
    startWeek ??= new Week(SDate.GetStartOfDay(this.rangeValue.begin), this.defaultRangeType);
    endWeek ??= new Week(SDate.GetStartOfDay(this.rangeValue.end ?? this.rangeValue.begin), this.defaultRangeType);
    //console.log(`[RangePicker] Processing Range Change \n${startWeek.ToLongString()}\n${endWeek.ToLongString()}`);
    const startRange = startWeek.ToDateRange(this.defaultRangeType);
    const endRange = endWeek.ToDateRange(this.defaultRangeType);
    // console.log(
    //   `[RangePicker] Processing Range Change: ${startRange.begin.format()} - ${endRange.end.format()} for range type ${
    //     this.defaultRangeType
    //   }`
    // );
    this.updateRange(new DateRange({ begin: startRange.begin, end: endRange.end }));
  }

  private getDefaultDateRange(): DateRange {
    console.log(`[RangePicker] Getting Default Range`, this.defaultRange, this.defaultRangeType);
    return this.defaultRange ?? new Week(0, this.defaultRangeType).ToDateRange(this.defaultRangeType);
  }

  updateRange(newRange?: DateRange) {
    newRange ??= this.getDefaultDateRange();
    if (
      !this.rangeValue ||
      !(newRange.begin.isSame(this.rangeValue.begin) && newRange.end.isSame(this.rangeValue.end))
    ) {
      this.controlValue = [SDate.ShiftTZToLocal(newRange.begin), SDate.ShiftTZToLocal(newRange.end)];
      this.rangeValue = newRange;
      // console.log(
      //   `[RangePicker] Updating Range: ${this.rangeValue.begin.format('MM/DD')} - ${this.rangeValue.end.format(
      //     'MM/DD'
      //   )}`
      // );
      this.DateRangeChange.emit(new DateRange(this.rangeValue));
    }
  }
  moveToToday() {
    const newRange = new DateRange(this.defaultRangeType);
    //console.log(`Moving to Today: ${newRange.begin.format()}-${newRange.end.format()}`);
    this.updateRange(newRange);
  }

  moveBackWeek() {
    // Enter key in input is triggering this so added a if to separate decision
    if (!this.dateRangeFocus) {
      const currentValue = new DateRange(this.rangeValue);
      currentValue.begin = currentValue.begin.add(-1, 'week');
      currentValue.end = currentValue.end.add(-1, 'week');
      //console.log(`[RangePicker] calling updateRange`);
      this.updateRange(currentValue);
    } else {
      this.ProcessRangeChange();
    }
  }

  moveBackWeeks() {
    // Enter key in input is triggering this so added a if to separate decision
    if (!this.dateRangeFocus) {
      const endDate = this.rangeValue.end;
      const weekRange = Math.ceil(endDate.diff(this.rangeValue.begin, 'week', true));
      const currentValue = new DateRange(this.rangeValue);
      currentValue.begin = currentValue.begin.add(-weekRange, 'week');
      currentValue.end = currentValue.end.add(-weekRange, 'week');
      //console.log(`[RangePicker] calling updateRange`);
      this.updateRange(currentValue);
    } else {
      this.ProcessRangeChange();
    }
  }

  moveForwardWeek() {
    //console.log('Forward a week');
    const currentValue = new DateRange(this.rangeValue);
    currentValue.begin = currentValue.begin.add(1, 'week');
    currentValue.end = currentValue.end.add(1, 'week');
    //console.log(`[RangePicker] calling updateRange`);
    this.updateRange(currentValue);
  }

  moveForwardWeeks() {
    //console.log('Forward a week');
    const endDate = this.rangeValue.end;
    const weekRange = Math.ceil(endDate.diff(this.rangeValue.begin, 'week', true));
    const currentValue = new DateRange(this.rangeValue);
    currentValue.begin = currentValue.begin.add(weekRange, 'week');
    currentValue.end = currentValue.end.add(weekRange, 'week');
    console.log(`[RangePicker] calling updateRange`);
    this.updateRange(currentValue);
  }

  getRange(): DateRange {
    return new DateRange(this.rangeValue);
  }
  /*
  setFocusTracking(newFocusState: boolean) {
    if (!newFocusState) {
      //console.log(`Focus changed calling ProcessRange`);
      this.ProcessRangeChange();
    }
    this.dateRangeFocus = newFocusState;
  }
*/
  onChange() {
    //console.log(`${this.fieldName.toString()}: onChange`, this.controlValue);
    if (this.controlValue[1] === null) return;
    this.rangeValue = new DateRange({
      begin: SDate.newUtc(this.controlValue[0]),
      end: SDate.newUtc(this.controlValue[1])
    });
    this.ProcessRangeChange();
    if (this.calendarVisible) {
      this.calendarVisible = false;
      this.calendar?.toggle();
    }
  }
}
